import React from "react";
import {Table} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {nf} from "../../util/numberFormat";
import {LeasePackageDto} from "../../model/vehicle/vehicle";

interface LeasePackageDataTableProps {
    leasePackage: LeasePackageDto;
}

export function LeasePackageDataTable(props: LeasePackageDataTableProps) {
    const {t, i18n} = useTranslation();

    function formatCurrency(value: number | undefined | null): string {
        if (value === undefined || value === null || isNaN(value)) {
            return 'N/A';
        }
        return nf(value, i18n.language, 2) + ' €';
    }

    function formatDate(dateString: string | undefined | null): string {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString(i18n.language);
        } catch {
            return dateString;
        }
    }

    function formatBoolean(value: boolean): string {
        return value ? 'Yes' : 'No';
    }

    function formatNumber(value: number | undefined | null): string {
        if (value === undefined || value === null || isNaN(value)) {
            return 'N/A';
        }
        return nf(value, i18n.language, 0);
    }

    function formatString(value: string | undefined | null): string {
        return value || 'N/A';
    }

    return (
        <Table striped bordered hover size="sm">
            <tbody>
            <tr>
                <td><strong>ID Order Vehicle Package</strong></td>
                <td>{props.leasePackage.id}</td>
            </tr>
            <tr>
                <td><strong>Offer ID</strong></td>
                <td>{props.leasePackage.offerId}</td>
            </tr>
            <tr>
                <td><strong>Active</strong></td>
                <td>{formatBoolean(props.leasePackage.active)}</td>
            </tr>
            <tr>
                <td><strong>KM per Year</strong></td>
                <td>{formatNumber(props.leasePackage.kmPerYear)} km</td>
            </tr>
            <tr>
                <td><strong>Duration (Months)</strong></td>
                <td>{props.leasePackage.durationMonths}</td>
            </tr>
            <tr>
                <td><strong>Tires</strong></td>
                <td>{props.leasePackage.tires}</td>
            </tr>
            <tr>
                <td><strong>Offer Expiry Date</strong></td>
                <td>{formatDate(props.leasePackage.offerExpiryDate)}</td>
            </tr>
            <tr>
                <td><strong>Net Retail Price</strong></td>
                <td>{formatCurrency(props.leasePackage.netRetailPriceEur)}</td>
            </tr>
            <tr>
                <td><strong>Gross Retail Price</strong></td>
                <td>{formatCurrency(props.leasePackage.grossRetailPriceEur)}</td>
            </tr>
            <tr>
                <td><strong>Acquisition Cost</strong></td>
                <td>{formatCurrency(props.leasePackage.acquisitionCostEur)}</td>
            </tr>
            <tr>
                <td><strong>Total Monthly Net Cost</strong></td>
                <td>{formatCurrency(props.leasePackage.totalMonthlyNetCostEur)}</td>
            </tr>
            <tr>
                <td><strong>Total Monthly Gross Cost</strong></td>
                <td>{formatCurrency(props.leasePackage.totalMonthlyGrossCostEur)}</td>
            </tr>
            <tr>
                <td><strong>Gross Comparison Cost</strong></td>
                <td>{formatCurrency(props.leasePackage.grossComparisonCostEur)}</td>
            </tr>
            <tr>
                <td><strong>Delivery Fee</strong></td>
                <td>{formatCurrency(props.leasePackage.deliveryFeeEur)}</td>
            </tr>
            <tr>
                <td><strong>User Price</strong></td>
                <td>{formatCurrency(props.leasePackage.userPrice)}</td>
            </tr>
            <tr>
                <td><strong>Costs More Kilometers</strong></td>
                <td>{formatCurrency(props.leasePackage.costsMoreKilometers)}</td>
            </tr>
            <tr>
                <td><strong>Reimbursement Less Kilometers</strong></td>
                <td>{formatCurrency(props.leasePackage.reimbursementLessKilometers)}</td>
            </tr>
            <tr>
                <td><strong>Excess Mileage Allowance</strong></td>
                <td>{formatCurrency(props.leasePackage.excessMileageAllowance)}</td>
            </tr>
            <tr>
                <td><strong>Exemption Limit Less Kilometers</strong></td>
                <td>{formatCurrency(props.leasePackage.exemptionLimitLessKilometers)}</td>
            </tr>
            <tr>
                <td><strong>Cap Fewer Kilometers</strong></td>
                <td>{formatCurrency(props.leasePackage.capFewerKilometers)}</td>
            </tr>
            <tr>
                <td><strong>Cap Extra Kilometers</strong></td>
                <td>{formatCurrency(props.leasePackage.capExtraKilometers)}</td>
            </tr>
            <tr>
                <td><strong>Vehicle Insurance Included</strong></td>
                <td>{formatBoolean(props.leasePackage.vehicleInsuranceIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Maintenance & Servicing Included</strong></td>
                <td>{formatBoolean(props.leasePackage.maintenanceAndServicingIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Tire Package Included</strong></td>
                <td>{formatBoolean(props.leasePackage.tirePackageIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Tire Change Included</strong></td>
                <td>{formatBoolean(props.leasePackage.tireChangeIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Broadcasting Fee Included</strong></td>
                <td>{formatBoolean(props.leasePackage.broadcastingFeeIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Accident Prevention Included</strong></td>
                <td>{formatBoolean(props.leasePackage.accidentPreventionIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Cancellation & Service Fee Included</strong></td>
                <td>{formatBoolean(props.leasePackage.cancellationAndServiceFeeIncluded)}</td>
            </tr>
            <tr>
                <td><strong>Rate Insurance Included</strong></td>
                <td>{formatBoolean(props.leasePackage.rateInsuranceIncluded)}</td>
            </tr>

            {/* Supplier Information Section */}

            <tr>
                <td><strong>Registration Supplier</strong></td>
                <td>{formatString(props.leasePackage.registrationSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Finance Rate Supplier</strong></td>
                <td>{formatString(props.leasePackage.financeRateSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Vehicle Insurance Supplier</strong></td>
                <td>{formatString(props.leasePackage.vehicleInsuranceSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Maintenance & Servicing Supplier</strong></td>
                <td>{formatString(props.leasePackage.maintenanceAndServicingSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Tire Package Supplier</strong></td>
                <td>{formatString(props.leasePackage.tirePackageSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Tire Change Supplier</strong></td>
                <td>{formatString(props.leasePackage.tireChangeSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Broadcasting Fee Supplier</strong></td>
                <td>{formatString(props.leasePackage.broadcastingFeeSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Accident Prevention Supplier</strong></td>
                <td>{formatString(props.leasePackage.accidentPreventionSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Cancellation & Service Fee Supplier</strong></td>
                <td>{formatString(props.leasePackage.cancellationAndServiceFeeSupplier)}</td>
            </tr>
            <tr>
                <td><strong>Rate Insurance Supplier</strong></td>
                <td>{formatString(props.leasePackage.rateInsuranceSupplier)}</td>
            </tr>

            {/*Suppliers Prices*/}
            <tr>
                <td><strong>Delivery Price</strong></td>
                <td>{formatCurrency(props.leasePackage.deliveryPrice)}</td>
            </tr>
            <tr>
                <td><strong>Registration Price</strong></td>
                <td>{formatCurrency(props.leasePackage.registrationPrice)}</td>
            </tr>
            <tr>
                <td><strong>Finance rate Price</strong></td>
                <td>{formatCurrency(props.leasePackage.financeRatePrice)}</td>
            </tr>
            <tr>
                <td><strong>Vehicle Insurance Price</strong></td>
                <td>{formatCurrency(props.leasePackage.vehicleInsurancePrice)}</td>
            </tr>
            <tr>
                <td><strong>Maintenance and Service Price</strong></td>
                <td>{formatCurrency(props.leasePackage.maintenanceAndServicingPrice)}</td>
            </tr>
            <tr>
                <td><strong>Tire Package Price</strong></td>
                <td>{formatCurrency(props.leasePackage.tirePackagePrice)}</td>
            </tr>
            <tr>
                <td><strong>Tire Change Price</strong></td>
                <td>{formatCurrency(props.leasePackage.tireChangePrice)}</td>
            </tr>
            <tr>
                <td><strong>Broadcasting Fee Price</strong></td>
                <td>{formatCurrency(props.leasePackage.broadcastingFeePrice)}</td>
            </tr>
            <tr>
                <td><strong>Accident Prevention Price</strong></td>
                <td>{formatCurrency(props.leasePackage.accidentPreventionPrice)}</td>
            </tr>
            <tr>
                <td><strong>Cancellation and Service Fee Price</strong></td>
                <td>{formatCurrency(props.leasePackage.cancellationAndServiceFeePrice)}</td>
            </tr>
            <tr>
                <td><strong>Rate Insurance Price</strong></td>
                <td>{formatCurrency(props.leasePackage.rateInsurancePrice)}</td>
            </tr>
            </tbody>
        </Table>
    );
}